import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Edit3, MapPin, Bell, Repeat } from "lucide-react";
import { api } from "../api";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { CAL } from "../constants/testIds";

// ─── Palette (approved only) ─────────────────────────────────────────────
const C = {
  bg:        "#0a2e0a",   // dark forest
  card:      "#0f3d0f",   // cards
  mosaic1:   "#1a5c1a",   // primary green
  mosaic2:   "#4a9a4a",   // hover/secondary
  high1:     "#CEF0CE",   // primary high-emphasis
  high2:     "#DFF2DF",   // subtle high-emphasis
  white:     "#ffffff",
};
const EVENT_COLORS = [C.bg, C.card, C.mosaic1, C.mosaic2, C.high1, C.high2];
const EVENT_TYPES = ["general", "prayer", "reminder", "hajj", "zakat"];
const REMINDERS = [
  ["", "None"], ["5", "5 min"], ["10", "10 min"], ["15", "15 min"],
  ["30", "30 min"], ["60", "1 hour"], ["1440", "1 day"],
];
const RECURRING = [
  ["", "None"], ["FREQ=DAILY", "Daily"], ["FREQ=WEEKLY", "Weekly"],
  ["FREQ=MONTHLY", "Monthly"], ["FREQ=YEARLY", "Yearly"],
];
const VIEWS = [["month", "Month"], ["week", "Week"], ["day", "Day"], ["agenda", "Agenda"]];
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ─── Date helpers (no external dep) ──────────────────────────────────────
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; };
const endOfWeek = (d) => { const x = endOfDay(d); x.setDate(x.getDate() + (6 - x.getDay())); return x; };
const startOfMonth = (d) => { const x = startOfDay(d); x.setDate(1); return x; };
const endOfMonth = (d) => { const x = startOfMonth(d); x.setMonth(x.getMonth() + 1); x.setDate(0); x.setHours(23,59,59,999); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const isSameDay = (a, b) => a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const isSameMonth = (a, b) => a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth();
const isToday = (d) => isSameDay(d, new Date());
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const hhmm = (d) => `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
const monthLabel = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

// ─── Main page ───────────────────────────────────────────────────────────
export default function CalendarPage() {
  const { user } = useAuth();
  const loc = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(() => (window.innerWidth < 768 ? "month" : window.innerWidth < 1024 ? "week" : "month"));
  const [events, setEvents] = useState([]);
  const [prayerTimes, setPrayerTimes] = useState({});
  const [islamicDates, setIslamicDates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [quickCreate, setQuickCreate] = useState(null); // {date, time}
  const [daySheet, setDaySheet] = useState(null); // { date, events, islamicOnDay }
  const [detail, setDetail] = useState(null); // event to show
  const [loading, setLoading] = useState(true);
  const rangeKeyRef = useRef("");
  const [hijriByDate, setHijriByDate] = useState({}); // {"YYYY-MM-DD": "1 Muharram 1447"}

  // ─── Data loading (cached) ─────────────────────────────────────────────
  const { fromISO, toISO, cacheKey } = useMemo(() => {
    let from, to;
    if (view === "month") { from = startOfWeek(startOfMonth(currentDate)); to = endOfWeek(endOfMonth(currentDate)); }
    else if (view === "week") { from = startOfWeek(currentDate); to = endOfWeek(currentDate); }
    else if (view === "day") { from = startOfDay(currentDate); to = endOfDay(currentDate); }
    else { from = startOfDay(currentDate); to = addDays(currentDate, 30); }
    return {
      fromISO: from.toISOString(),
      toISO: to.toISOString(),
      cacheKey: `calendar_events_${user?.id || "anon"}_${ymd(from)}_${ymd(to)}`,
    };
  }, [view, currentDate, user?.id]);

  const load = useCallback(async () => {
    // 5-minute cache
    try {
      const c = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (c && Date.now() - c.ts < 5 * 60_000) {
        setEvents(c.data.events || []);
        setPrayerTimes(c.data.prayer_times || {});
        setIslamicDates(c.data.islamic_dates || []);
        setLoading(false);
        return;
      }
    } catch { /* noop */ }

    setLoading(true);
    try {
      const r = await api.post("/calendar/events/list", {
        date_from: fromISO,
        date_to: toISO,
        include_prayer_times: true,
        include_islamic_dates: true,
      });
      setEvents(r.data.events || []);
      setPrayerTimes(r.data.prayer_times || {});
      setIslamicDates(r.data.islamic_dates || []);
      try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: r.data })); } catch { /* noop */ }
    } catch (e) {
      toast.error("Could not load calendar events");
    } finally {
      setLoading(false);
    }
  }, [cacheKey, fromISO, toISO]);

  useEffect(() => {
    // Only reload when the visible range actually changes
    const k = `${fromISO}|${toISO}`;
    if (rangeKeyRef.current === k) return;
    rangeKeyRef.current = k;
    load();
  }, [fromISO, toISO, load]);

  // Realtime subscription — refresh on any change to this user's rows
  useEffect(() => {
    if (!user?.id) return;
    const ch = supabase.channel(`calendar-${user.id}`).on(
      "postgres_changes",
      { event: "*", schema: "public", table: "calendar_events", filter: `user_id=eq.${user.id}` },
      () => { try { localStorage.removeItem(cacheKey); } catch { /* noop */ } load(); },
    ).subscribe();
    return () => { try { supabase.removeChannel(ch); } catch { /* noop */ } };
  }, [user?.id, cacheKey, load]);

  // Auto-prefill from voice intent
  useEffect(() => {
    const intent = loc.state?.intent;
    if (intent?.module === "calendar" && intent?.action === "create_event") {
      const f = intent.extracted_fields || {};
      const start = inferDateTime(f.date, f.time);
      setEditingEvent({ title: f.title || "Event", start_at: start, event_type: "general", color: C.mosaic1 });
      setShowModal(true);
    }
  }, [loc.state]);

  // ─── Hijri per-day (batch fetch for visible range) ─────────────────────
  useEffect(() => {
    // Cache Hijri per YYYY-MM globally (never changes)
    const cacheKey = `hijri_${ymd(startOfMonth(currentDate))}`;
    const cached = (() => { try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; } })();
    if (cached) { setHijriByDate((h) => ({ ...h, ...cached })); return; }
    (async () => {
      try {
        const y = currentDate.getFullYear();
        const m = currentDate.getMonth() + 1;
        const r = await fetch(`https://api.aladhan.com/v1/gToHCalendar/${String(m).padStart(2,"0")}/${y}`);
        const j = await r.json();
        if (j.code === 200) {
          const map = {};
          j.data.forEach((day) => {
            const g = day.gregorian.date;  // DD-MM-YYYY
            const [dd, mm, yy] = g.split("-");
            const iso = `${yy}-${mm}-${dd}`;
            map[iso] = `${day.hijri.day} ${day.hijri.month.en}`;
          });
          localStorage.setItem(cacheKey, JSON.stringify(map));
          setHijriByDate((h) => ({ ...h, ...map }));
        }
      } catch { /* noop */ }
    })();
  }, [currentDate]);

  // ─── Navigation controls ───────────────────────────────────────────────
  const navigate = (dir) => {
    if (view === "month") setCurrentDate(addMonths(currentDate, dir));
    else if (view === "week") setCurrentDate(addDays(currentDate, 7 * dir));
    else if (view === "day") setCurrentDate(addDays(currentDate, dir));
    else setCurrentDate(addDays(currentDate, 30 * dir));
  };

  // ─── Save / delete handlers ────────────────────────────────────────────
  const saveEvent = async (payload) => {
    try {
      if (payload.event_id) {
        await api.post("/calendar/events/update", payload);
        toast.success("Event updated Alhamdulillah");
      } else {
        await api.post("/calendar/events/create", payload);
        toast.success("Event created Insha'Allah");
      }
      try { localStorage.removeItem(cacheKey); } catch { /* noop */ }
      setShowModal(false);
      setEditingEvent(null);
      setQuickCreate(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save event");
    }
  };
  const deleteEvent = async (event_id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.post("/calendar/events/delete", { event_id });
      toast.success("Event deleted");
      try { localStorage.removeItem(cacheKey); } catch { /* noop */ }
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  // ─── Grouped events for view rendering ─────────────────────────────────
  const eventsByDate = useMemo(() => {
    const m = {};
    events.forEach((ev) => {
      const key = ymd(new Date(ev.start_at));
      (m[key] = m[key] || []).push(ev);
    });
    return m;
  }, [events]);

  const islamicByDate = useMemo(() => {
    const m = {};
    islamicDates.forEach((d) => { if (d.date) (m[d.date] = m[d.date] || []).push(d); });
    return m;
  }, [islamicDates]);

  return (
    <div className="dnya-reveal" style={{ color: C.white }}>
      <CalendarHeader
        currentDate={currentDate}
        view={view}
        setView={setView}
        navigate={navigate}
        setCurrentDate={setCurrentDate}
        onNew={() => { setEditingEvent(null); setShowModal(true); }}
        googleConnected={!!user?.google_calendar_connected}
      />

      {loading ? (
        <Skeleton />
      ) : view === "month" ? (
        <MonthView
          currentDate={currentDate}
          eventsByDate={eventsByDate}
          islamicByDate={islamicByDate}
          hijriByDate={hijriByDate}
          prayerTimes={prayerTimes}
          onCellClick={(d) => {
            const iso = ymd(d);
            setDaySheet({ date: d, events: eventsByDate[iso] || [], islamic: islamicByDate[iso] || [] });
          }}
          onEventClick={(ev) => setDetail(ev)}
        />
      ) : view === "week" ? (
        <TimeGridView
          days={7}
          startDate={startOfWeek(currentDate)}
          events={events}
          prayerTimes={prayerTimes}
          hijriByDate={hijriByDate}
          onSlotClick={(d, hour) => setQuickCreate({ date: d, time: `${String(hour).padStart(2,"0")}:00` })}
          onEventClick={(ev) => setDetail(ev)}
        />
      ) : view === "day" ? (
        <TimeGridView
          days={1}
          startDate={startOfDay(currentDate)}
          events={events}
          prayerTimes={prayerTimes}
          hijriByDate={hijriByDate}
          onSlotClick={(d, hour) => setQuickCreate({ date: d, time: `${String(hour).padStart(2,"0")}:00` })}
          onEventClick={(ev) => setDetail(ev)}
        />
      ) : (
        <AgendaView
          startDate={currentDate}
          events={events}
          islamicDates={islamicDates}
          prayerTimes={prayerTimes}
          hijriByDate={hijriByDate}
          onEventClick={(ev) => setDetail(ev)}
        />
      )}

      {quickCreate && (
        <QuickCreatePopup
          initial={quickCreate}
          onClose={() => setQuickCreate(null)}
          onSave={saveEvent}
          onMoreOptions={(seed) => { setQuickCreate(null); setEditingEvent(seed); setShowModal(true); }}
        />
      )}

      {showModal && (
        <EventModal
          initial={editingEvent}
          onClose={() => { setShowModal(false); setEditingEvent(null); }}
          onSave={saveEvent}
        />
      )}

      {detail && (
        <EventDetail
          event={detail}
          hijri={hijriByDate[ymd(new Date(detail.start_at))]}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditingEvent(detail); setDetail(null); setShowModal(true); }}
          onDelete={() => deleteEvent(detail.id)}
        />
      )}

      {daySheet && (
        <DaySheet
          date={daySheet.date}
          events={daySheet.events}
          islamic={daySheet.islamic}
          prayerTimes={prayerTimes}
          hijri={hijriByDate[ymd(daySheet.date)]}
          onClose={() => setDaySheet(null)}
          onAddEvent={() => {
            setEditingEvent({ start_at: new Date(ymd(daySheet.date) + "T09:00:00").toISOString(), event_type: "general", color: "#1a5c1a" });
            setDaySheet(null);
            setShowModal(true);
          }}
          onEventClick={(ev) => { setDaySheet(null); setDetail(ev); }}
        />
      )}
    </div>
  );
}

// ─── Header (navigation + view tabs) ─────────────────────────────────────
function CalendarHeader({ currentDate, view, setView, navigate, setCurrentDate, onNew, googleConnected }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button data-testid={CAL.navPrev} onClick={() => navigate(-1)} aria-label="Previous" style={{ ...iconBtn, color: "var(--text-primary)" }}><ChevronLeft size={20} /></button>
        <button data-testid={CAL.navToday} onClick={() => setCurrentDate(new Date())} style={{ ...ghostBtn, color: "var(--text-primary)" }}>Today</button>
        <button data-testid={CAL.navNext} onClick={() => navigate(1)} aria-label="Next" style={{ ...iconBtn, color: "var(--text-primary)" }}><ChevronRight size={20} /></button>
        <h1 data-testid={CAL.monthLabel} className="font-dnya" style={{ fontFamily: "Cinzel, serif", fontSize: 24, color: "var(--text-primary)", margin: 0, flex: 1, minWidth: 180 }}>
          {monthLabel(currentDate)}
        </h1>
        <div data-testid={CAL.gcalStatus} style={{ fontSize: 12, color: googleConnected ? C.high1 : C.mosaic2, display: "flex", alignItems: "center", gap: 6 }}>
          {googleConnected ? (<><span style={{ width: 8, height: 8, borderRadius: 999, background: C.high1, boxShadow: `0 0 6px ${C.high1}` }} />Google Calendar synced</>) : (
            <a href="/api/auth/google?scope=calendar" style={{ color: C.mosaic2, textDecoration: "underline" }}>Connect Google Calendar</a>
          )}
        </div>
        <button data-testid={CAL.newEventBtn} onClick={onNew} style={primaryBtn}><Plus size={16} /> New Event</button>
      </div>

      <div style={{ display: "flex", gap: 0, marginTop: 12, borderBottom: `1px solid ${C.mosaic1}` }}>
        {VIEWS.map(([id, label]) => (
          <button
            key={id}
            data-testid={id === "month" ? CAL.viewMonth : id === "week" ? CAL.viewWeek : id === "day" ? CAL.viewDay : CAL.viewAgenda}
            onClick={() => setView(id)}
            style={{
              padding: "10px 18px", background: view === id ? C.mosaic1 : "transparent",
              color: view === id ? "#ffffff" : "var(--text-primary)", border: 0,
              borderBottom: view === id ? `2px solid ${C.high1}` : "2px solid transparent",
              cursor: "pointer", fontSize: 13, fontWeight: 600, letterSpacing: "0.05em",
              transition: "background 200ms ease, color 200ms ease",
              minHeight: 40,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Month view ──────────────────────────────────────────────────────────
function MonthView({ currentDate, eventsByDate, islamicByDate, hijriByDate, onCellClick, onEventClick }) {
  const first = startOfWeek(startOfMonth(currentDate));
  const cells = Array.from({ length: 42 }, (_, i) => addDays(first, i));

  return (
    <div style={{ border: `1px solid ${C.mosaic1}`, borderRadius: 10, overflow: "hidden", background: C.card }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: "var(--dnya-bg)" }}>
        {WEEKDAY_SHORT.map((d) => (
          <div key={d} style={{ padding: "10px 8px", fontSize: 11, color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "center", fontWeight: 600 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridAutoRows: "minmax(96px, 1fr)" }}>
        {cells.map((d) => {
          const iso = ymd(d);
          const evs = eventsByDate[iso] || [];
          const iDates = islamicByDate[iso] || [];
          const inMonth = isSameMonth(d, currentDate);
          const today = isToday(d);
          const wd = d.getDay();
          const sunnah = (wd === 1 || wd === 4);
          return (
            <div
              key={iso}
              data-testid={CAL.dayCell}
              onClick={() => onCellClick(d)}
              style={{
                borderTop: `1px solid ${C.mosaic1}`, borderRight: `1px solid ${C.mosaic1}`,
                padding: 6, cursor: "pointer",
                background: today ? C.mosaic1 + "22" : "transparent",
                minHeight: 96, position: "relative",
                opacity: inMonth ? 1 : 0.5,
                transition: "background 150ms ease",
              }}
            >
              {/* Prayer-time dots strip */}
              <div style={{ display: "flex", gap: 3, marginBottom: 4, opacity: 0.7 }}>
                {["Fajr","Dhuhr","Asr","Maghrib","Isha"].map((p) => (
                  <span key={p} style={{ width: 4, height: 4, borderRadius: 999, background: C.mosaic1 }} />
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                {today ? (
                  <span data-testid={CAL.todayCircle} style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 24, height: 24, borderRadius: "50%",
                    background: C.high1, color: C.bg, fontSize: 12, fontWeight: 700,
                  }}>{d.getDate()}</span>
                ) : (
                  <span style={{ fontSize: 13, color: inMonth ? C.white : C.mosaic2, fontWeight: 500 }}>{d.getDate()}</span>
                )}
                {sunnah && <span title="Sunnah fast day" style={{ color: C.mosaic2, fontSize: 9 }}>★</span>}
              </div>
              <div style={{ fontSize: 9, color: C.mosaic2, marginTop: 2, lineHeight: 1.1 }}>{hijriByDate[iso] || ""}</div>
              {iDates.map((ii) => (
                <div key={`${iso}-${ii.name}`} style={{ fontSize: 10, color: C.high1, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  ☾ {ii.name}
                </div>
              ))}
              {evs.slice(0, 3).map((ev) => (
                <button
                  key={ev.id}
                  data-testid={CAL.eventCard}
                  onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left", border: 0,
                    background: ev.color || C.mosaic1, color: C.white,
                    padding: "3px 6px", borderRadius: 3, marginTop: 3,
                    fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    cursor: "pointer",
                  }}
                >
                  {ev.title}
                </button>
              ))}
              {evs.length > 3 && <div style={{ fontSize: 10, color: C.mosaic2, marginTop: 3 }}>+{evs.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week / Day time-grid view ───────────────────────────────────────────
function TimeGridView({ days, startDate, events, prayerTimes, hijriByDate, onSlotClick, onEventClick }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const cols = Array.from({ length: days }, (_, i) => addDays(startDate, i));
  const now = new Date();
  const nowPct = ((now.getHours() * 60 + now.getMinutes()) / (24 * 60)) * 100;

  return (
    <div style={{ display: "grid", gridTemplateColumns: `60px repeat(${days}, 1fr)`, border: `1px solid ${C.mosaic1}`, borderRadius: 10, background: C.card, overflow: "hidden" }}>
      {/* Day headers */}
      <div />
      {cols.map((d) => (
        <div key={ymd(d)} style={{ padding: "10px 8px", borderLeft: `1px solid ${C.mosaic1}`, textAlign: "center", background: C.bg }}>
          <div style={{ fontSize: 11, color: C.high1, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>
            {WEEKDAY_SHORT[d.getDay()]}
          </div>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 30, height: 30, borderRadius: "50%", marginTop: 4,
            background: isToday(d) ? C.high1 : "transparent",
            color: isToday(d) ? C.bg : C.white, fontSize: 15, fontWeight: 600,
          }}>{d.getDate()}</div>
          <div style={{ fontSize: 9, color: C.mosaic2, marginTop: 2 }}>{hijriByDate[ymd(d)] || ""}</div>
        </div>
      ))}
      {/* Grid body */}
      <div style={{ position: "relative" }}>
        {hours.map((h) => (
          <div key={h} style={{ height: 40, borderTop: `1px solid ${C.mosaic1}22`, padding: "2px 6px", fontSize: 10, color: C.mosaic2, textAlign: "right" }}>
            {String(h).padStart(2,"0")}:00
          </div>
        ))}
      </div>
      {cols.map((d, colIdx) => {
        const iso = ymd(d);
        const dayEvents = events.filter((ev) => isSameDay(new Date(ev.start_at), d));
        return (
          <div key={iso} style={{ position: "relative", borderLeft: `1px solid ${C.mosaic1}` }}>
            {hours.map((h) => (
              <div
                key={h}
                onClick={() => onSlotClick(d, h)}
                style={{ height: 40, borderTop: `1px solid ${C.mosaic1}22`, cursor: "pointer", transition: "background 150ms ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.mosaic1 + "22"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              />
            ))}
            {/* Prayer bars (only for today) */}
            {isToday(d) && Object.entries(prayerTimes).map(([name, t]) => {
              const [h, m] = t.split(":").map(Number);
              const top = ((h * 60 + m) / (24 * 60)) * (24 * 40);
              return (
                <div key={name} style={{
                  position: "absolute", left: 2, right: 2, top,
                  height: 16, background: C.mosaic1 + "88",
                  borderRadius: 3, fontSize: 9, color: C.high1,
                  padding: "1px 6px", display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span style={{ fontWeight: 600 }}>{name}</span> <span>{t}</span>
                </div>
              );
            })}
            {/* Events positioned */}
            {dayEvents.map((ev) => {
              const s = new Date(ev.start_at);
              const e = ev.end_at ? new Date(ev.end_at) : new Date(s.getTime() + 60 * 60 * 1000);
              const top = ((s.getHours() * 60 + s.getMinutes()) / (24 * 60)) * (24 * 40);
              const height = Math.max(20, ((e - s) / 60000 / (24 * 60)) * (24 * 40));
              return (
                <button
                  key={ev.id}
                  onClick={(evClick) => { evClick.stopPropagation(); onEventClick(ev); }}
                  style={{
                    position: "absolute", left: 4, right: 4, top, height,
                    background: ev.color || C.mosaic1, color: C.white,
                    border: 0, borderRadius: 4, padding: "3px 6px",
                    fontSize: 11, textAlign: "left", cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  <div style={{ fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</div>
                  <div style={{ fontSize: 9, opacity: 0.9 }}>{hhmm(s)}–{hhmm(e)}</div>
                </button>
              );
            })}
            {/* Current-time line (only on today's column) */}
            {isToday(d) && colIdx === cols.findIndex((x) => isToday(x)) && (
              <div style={{
                position: "absolute", left: 0, right: 0, top: (nowPct / 100) * (24 * 40),
                height: 2, background: C.high1, boxShadow: `0 0 4px ${C.high1}`, zIndex: 5,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Agenda view ─────────────────────────────────────────────────────────
function AgendaView({ startDate, events, islamicDates, prayerTimes, hijriByDate, onEventClick }) {
  // Group events + islamic + prayer stubs by date
  const groups = {};
  events.forEach((ev) => {
    const iso = ymd(new Date(ev.start_at));
    (groups[iso] = groups[iso] || []).push({ kind: "event", ev });
  });
  islamicDates.forEach((d) => {
    if (!d.date) return;
    (groups[d.date] = groups[d.date] || []).push({ kind: "islamic", name: d.name });
  });
  // Add prayer times only for TODAY's group
  const todayIso = ymd(new Date());
  if (Object.keys(prayerTimes).length) {
    Object.entries(prayerTimes).forEach(([name, t]) => {
      (groups[todayIso] = groups[todayIso] || []).push({ kind: "prayer", name, t });
    });
  }
  const sortedDates = Object.keys(groups).sort();
  if (sortedDates.length === 0) {
    return <div style={emptyStyle}>No events in this period — Insha&apos;Allah you will add some soon</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {sortedDates.map((iso) => {
        const d = new Date(iso + "T00:00:00");
        return (
          <div key={iso}>
            <div style={{ fontSize: 12, color: C.high1, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
              {d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div style={{ fontSize: 10, color: C.mosaic2, marginBottom: 8 }}>{hijriByDate[iso]}</div>
            {groups[iso].map((item) => {
              if (item.kind === "islamic") return (
                <div key={`${iso}-islamic-${item.name}`} style={{ padding: "10px 14px", background: C.mosaic1, color: C.high1, borderRadius: 6, marginBottom: 6, fontSize: 13, fontWeight: 500 }}>☾ {item.name}</div>
              );
              if (item.kind === "prayer") return (
                <div key={`${iso}-prayer-${item.name}`} style={{ padding: "8px 14px", background: C.card, borderLeft: `3px solid ${C.mosaic1}`, borderRadius: 4, marginBottom: 6, fontSize: 12, color: C.high1, display: "flex", gap: 12 }}>
                  <span style={{ fontWeight: 600 }}>{item.name}</span><span style={{ color: C.mosaic2 }}>{item.t}</span>
                </div>
              );
              const ev = item.ev;
              const s = new Date(ev.start_at);
              const e = ev.end_at ? new Date(ev.end_at) : null;
              return (
                <button
                  key={ev.id}
                  data-testid={CAL.eventCard}
                  onClick={() => onEventClick(ev)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: C.card, border: `1px solid ${C.mosaic1}`, borderRadius: 6, marginBottom: 6, width: "100%", cursor: "pointer", textAlign: "left" }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: ev.color || C.mosaic1, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: C.mosaic2, minWidth: 90 }}>{hhmm(s)}{e ? `–${hhmm(e)}` : ""}</span>
                  <span style={{ flex: 1, fontSize: 13, color: C.white }}>{ev.title}</span>
                  {ev.location && <span style={{ fontSize: 11, color: C.mosaic2 }}>{ev.location}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─── Quick create popup ──────────────────────────────────────────────────
function QuickCreatePopup({ initial, onClose, onSave, onMoreOptions }) {
  const [title, setTitle] = useState("");
  const dateISO = ymd(initial.date);
  const [time, setTime] = useState(initial.time || "09:00");
  const handleSave = () => {
    if (!title.trim()) return toast.error("Title is required");
    const start = new Date(`${dateISO}T${time}:00`).toISOString();
    onSave({ title: title.trim(), start_at: start, event_type: "general", color: C.mosaic1 });
  };
  return (
    <div onClick={onClose} style={overlayStyle}>
      <div data-testid={CAL.quickCreate} onClick={(e) => e.stopPropagation()} style={{ ...cardStyle, width: "min(90vw, 340px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ color: C.high1, fontSize: 13, fontWeight: 600 }}>Quick add</div>
          <button data-testid={CAL.quickClose} onClick={onClose} style={{ ...iconBtn, minWidth: 32, minHeight: 32 }}><X size={14} /></button>
        </div>
        <input
          data-testid={CAL.quickTitle}
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="Add title"
          style={inpStyle}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 12, color: C.high1 }}>
          <span>{initial.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ ...inpStyle, width: 110, padding: "6px 8px", fontSize: 12 }} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button data-testid={CAL.quickSave} onClick={handleSave} style={{ ...primaryBtn, flex: 1 }}>Save</button>
          <button data-testid={CAL.quickMore} onClick={() => onMoreOptions({ title, start_at: `${dateISO}T${time}:00`, event_type: "general", color: C.mosaic1 })} style={ghostBtn}>More options</button>
        </div>
      </div>
    </div>
  );
}

// ─── Full event modal ────────────────────────────────────────────────────
function EventModal({ initial, onClose, onSave }) {
  const seed = initial || {};
  const startDefault = seed.start_at ? new Date(seed.start_at) : new Date();
  const endDefault = seed.end_at ? new Date(seed.end_at) : new Date(startDefault.getTime() + 60 * 60 * 1000);
  const [title, setTitle] = useState(seed.title || "");
  const [startDate, setStartDate] = useState(ymd(startDefault));
  const [startTime, setStartTime] = useState(hhmm(startDefault));
  const [endDate, setEndDate] = useState(ymd(endDefault));
  const [endTime, setEndTime] = useState(hhmm(endDefault));
  const [allDay, setAllDay] = useState(!!seed.all_day);
  const [location, setLocation] = useState(seed.location || "");
  const [description, setDescription] = useState(seed.description || "");
  const [eventType, setEventType] = useState(seed.event_type || "general");
  const [color, setColor] = useState(seed.color || C.mosaic1);
  const [reminder, setReminder] = useState(String(seed.reminder_minutes ?? 10));
  const [recurring, setRecurring] = useState(seed.recurrence_rule || "");
  const [hijri, setHijri] = useState(seed.hijri_date || "");

  useEffect(() => {
    (async () => {
      try {
        const [y, m, d] = startDate.split("-");
        const r = await fetch(`https://api.aladhan.com/v1/gToH/${d}-${m}-${y}`);
        const j = await r.json();
        if (j.code === 200) setHijri(`${j.data.hijri.day} ${j.data.hijri.month.en} ${j.data.hijri.year}`);
      } catch { /* noop */ }
    })();
  }, [startDate]);

  const handleSave = () => {
    if (!title.trim()) return toast.error("Title is required");
    const start = allDay ? `${startDate}T00:00:00` : `${startDate}T${startTime}:00`;
    const end = allDay ? `${endDate}T23:59:59` : `${endDate}T${endTime}:00`;
    const payload = {
      title: title.trim(),
      start_at: new Date(start).toISOString(),
      end_at: new Date(end).toISOString(),
      description: description || null,
      location: location || null,
      event_type: eventType,
      all_day: allDay,
      color,
      reminder_minutes: reminder === "" ? null : Number(reminder),
      is_recurring: !!recurring,
      recurrence_rule: recurring || null,
    };
    if (seed.id) payload.event_id = seed.id;
    onSave(payload);
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div data-testid={CAL.modal} onClick={(e) => e.stopPropagation()} style={{ ...cardStyle, width: "min(95vw, 480px)", maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div className="font-dnya" style={{ color: C.high1, fontFamily: "Cinzel, serif", fontSize: 18 }}>{seed.id ? "Edit event" : "New event"}</div>
          <button onClick={onClose} style={iconBtn} aria-label="Close"><X size={16} /></button>
        </div>

        <input data-testid={CAL.modalTitle} autoFocus placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} style={inpStyle} />

        <div style={{ display: "grid", gridTemplateColumns: allDay ? "1fr" : "1fr 1fr", gap: 10, marginTop: 10 }}>
          <input data-testid={CAL.modalStartDate} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inpStyle} />
          {!allDay && <input data-testid={CAL.modalStartTime} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inpStyle} />}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: allDay ? "1fr" : "1fr 1fr", gap: 10, marginTop: 8 }}>
          <input data-testid={CAL.modalEndDate} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inpStyle} />
          {!allDay && <input data-testid={CAL.modalEndTime} type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inpStyle} />}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, fontSize: 13, color: C.high1, cursor: "pointer" }}>
          <input data-testid={CAL.modalAllDay} type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          All day
        </label>

        {hijri && <div style={{ fontSize: 11, color: C.mosaic2, marginTop: 8 }}>Hijri: {hijri}</div>}

        <div style={{ marginTop: 12, position: "relative" }}>
          <MapPin size={14} style={{ position: "absolute", left: 10, top: 12, color: C.mosaic2 }} />
          <input data-testid={CAL.modalLocation} placeholder="Location (optional)" value={location} onChange={(e) => setLocation(e.target.value)} style={{ ...inpStyle, paddingLeft: 32 }} />
        </div>

        <textarea data-testid={CAL.modalDescription} rows={3} placeholder="Notes (optional)" value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inpStyle, marginTop: 10, resize: "vertical" }} />

        {/* Event type pills */}
        <div style={{ marginTop: 14 }}>
          <div style={labelStyle}>Type</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {EVENT_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setEventType(t)}
                style={{
                  padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                  background: eventType === t ? C.mosaic1 : "transparent",
                  color: eventType === t ? C.white : C.high1,
                  border: `1px solid ${eventType === t ? C.high1 : C.mosaic1}`,
                  cursor: "pointer", textTransform: "capitalize", minHeight: 32,
                }}
              >{t}</button>
            ))}
          </div>
        </div>

        {/* Colour swatches */}
        <div style={{ marginTop: 14 }}>
          <div style={labelStyle}>Colour</div>
          <div style={{ display: "flex", gap: 8 }}>
            {EVENT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                aria-label={`Colour ${c}`}
                style={{
                  width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer",
                  border: 0, boxShadow: color === c ? `0 0 0 3px ${C.bg}, 0 0 0 5px ${C.high1}` : `0 0 0 1px ${C.mosaic1}`,
                  transition: "box-shadow 150ms ease",
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
          <div>
            <div style={labelStyle}><Bell size={11} /> Reminder</div>
            <select data-testid={CAL.modalReminder} value={reminder} onChange={(e) => setReminder(e.target.value)} style={inpStyle}>
              {REMINDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}><Repeat size={11} /> Recurring</div>
            <select data-testid={CAL.modalRecurring} value={recurring} onChange={(e) => setRecurring(e.target.value)} style={inpStyle}>
              {RECURRING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button data-testid={CAL.modalCancel} onClick={onClose} style={{ ...ghostBtn, flex: 1 }}>Cancel</button>
          <button data-testid={CAL.modalSave} onClick={handleSave} style={{ ...primaryBtn, flex: 2 }}>{seed.id ? "Save changes" : "Save event"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Event detail popup ──────────────────────────────────────────────────
function EventDetail({ event, hijri, onClose, onEdit, onDelete }) {
  const s = new Date(event.start_at);
  const e = event.end_at ? new Date(event.end_at) : null;
  const readOnly = event.is_islamic_date || event.event_type === "islamic_date" || event.is_prayer_time;
  return (
    <div onClick={onClose} style={overlayStyle}>
      <div data-testid={CAL.eventDetail} onClick={(evClick) => evClick.stopPropagation()} style={{ ...cardStyle, width: "min(90vw, 380px)", padding: 0, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
        <div style={{ height: 4, background: event.color || C.mosaic1 }} />
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ color: C.white, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{event.title}</div>
            <button onClick={onClose} style={iconBtn} aria-label="Close"><X size={16} /></button>
          </div>
          <div style={{ color: C.high1, fontSize: 13, marginTop: 8 }}>
            {s.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            {!event.all_day && ` · ${hhmm(s)}${e ? `–${hhmm(e)}` : ""}`}
          </div>
          {hijri && <div style={{ color: C.mosaic2, fontSize: 11, marginTop: 3 }}>{hijri}</div>}
          {event.location && <div style={{ color: C.high2, fontSize: 12, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}><MapPin size={12} /> {event.location}</div>}
          {event.description && <div style={{ color: C.white, fontSize: 12, marginTop: 10, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{event.description}</div>}
          {event.event_type && (
            <div style={{ marginTop: 12, display: "inline-block", padding: "3px 10px", borderRadius: 999, background: C.mosaic1, color: C.high1, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {event.event_type.replace("_", " ")}
            </div>
          )}
          {!readOnly && (
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button data-testid={CAL.eventEdit} onClick={onEdit} style={{ ...ghostBtn, flex: 1 }}><Edit3 size={14} /> Edit</button>
              <button data-testid={CAL.eventDelete} onClick={onDelete} style={{ ...ghostBtn, flex: 1, borderColor: C.high2, color: C.high2 }}><Trash2 size={14} /> Delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.mosaic1}`, borderRadius: 10, padding: 40, textAlign: "center", color: C.mosaic2, fontSize: 13 }}>
      Loading calendar Insha&apos;Allah...
    </div>
  );
}

// ─── Small helpers ───────────────────────────────────────────────────────
function inferDateTime(dateStr, timeStr) {
  const now = new Date();
  let t = new Date(now);
  if (dateStr) {
    const s = dateStr.toLowerCase();
    if (s === "tomorrow") t.setDate(t.getDate() + 1);
    else if (s === "today") { /* keep */ }
    else { const parsed = new Date(dateStr); if (!isNaN(parsed)) t = parsed; }
  }
  if (timeStr) {
    const [h, m] = timeStr.split(":").map(Number);
    if (!isNaN(h)) t.setHours(h, m || 0, 0, 0);
  } else if (t.getHours() === now.getHours()) t.setHours(t.getHours() + 1, 0, 0, 0);
  return t.toISOString();
}

// ─── Shared styles (approved palette only) ───────────────────────────────
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(10,46,10,0.55)",
  backdropFilter: "blur(2px)", zIndex: 1500,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
};
const cardStyle = {
  background: C.card, border: `1px solid ${C.high1}`, borderRadius: 12,
  padding: 18, color: C.white,
};
const primaryBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  padding: "10px 18px", borderRadius: 8,
  background: C.mosaic1, color: C.white, border: `1px solid ${C.high1}`,
  cursor: "pointer", fontSize: 13, fontWeight: 600, minHeight: 40,
  transition: "background 200ms ease",
};
const ghostBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  padding: "10px 18px", borderRadius: 8,
  background: "transparent", color: C.high1, border: `1px solid ${C.high1}`,
  cursor: "pointer", fontSize: 13, fontWeight: 500, minHeight: 40,
  transition: "background 200ms ease",
};
const iconBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 40, height: 40, borderRadius: 8,
  background: "transparent", color: C.high1, border: `1px solid ${C.mosaic1}`,
  cursor: "pointer", padding: 0,
};
const inpStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 8,
  background: C.bg, color: C.white, border: `1px solid ${C.high1}`,
  outline: "none", fontSize: 14, boxSizing: "border-box",
};
const labelStyle = {
  fontSize: 11, color: C.high1, marginBottom: 6, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const emptyStyle = {
  color: C.mosaic2, fontSize: 14, padding: 40, textAlign: "center",
  border: `1px dashed ${C.mosaic1}`, borderRadius: 12,
};

// ─── Day sheet (bottom-slide sheet with events for a tapped day) ──────
function DaySheet({ date, events, islamic, prayerTimes, hijri, onClose, onAddEvent, onEventClick }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 499 }} />
      <div
        data-testid="calendar-day-sheet"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          height: "min(60vh, 640px)",
          background: "var(--bg-secondary)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          borderTop: "1px solid var(--border-color)",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.4)",
          zIndex: 500,
          transform: "translateY(0)",
          transition: "transform 300ms ease-in-out",
          display: "flex", flexDirection: "column",
          color: "var(--text-primary)",
        }}
      >
        <div style={{ width: 40, height: 4, background: "var(--text-muted)", borderRadius: 2, margin: "8px auto 4px" }} />
        <div style={{ height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", borderBottom: `1px solid ${C.mosaic1}` }}>
          <div>
            <div style={{ color: "var(--color-accent-1)", fontSize: 15, fontWeight: 500 }}>
              {date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
            {hijri && <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>{hijri}</div>}
          </div>
          <button
            data-testid="calendar-day-sheet-add"
            onClick={onAddEvent}
            style={{ background: C.mosaic1, color: "#ffffff", border: 0, borderRadius: 20, padding: "6px 14px", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, minHeight: 32 }}
          >
            + Add Event
          </button>
        </div>
        <div style={{ padding: "12px 16px", overflowY: "auto", flex: 1 }}>
          {(events.length === 0 && islamic.length === 0) ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, color: "var(--text-muted)", fontSize: 13 }}>
              <div>No events on this day</div>
              <button onClick={onAddEvent} style={{ marginTop: 12, background: "transparent", border: 0, color: "var(--color-accent-1)", fontSize: 12, cursor: "pointer" }}>+ Add Event</button>
            </div>
          ) : (
            <>
              {islamic.map((i, idx) => (
                <div key={`i-${date.toISOString().slice(0,10)}-${i.name || idx}`} style={{ background: C.mosaic1, color: "var(--color-accent-1)", padding: "10px 12px", borderRadius: 6, marginBottom: 8, fontSize: 13, fontWeight: 500 }}>
                  ☾ {i.name}
                </div>
              ))}
              {events.map((ev) => {
                const s = new Date(ev.start_at);
                const e = ev.end_at ? new Date(ev.end_at) : null;
                return (
                  <button
                    key={ev.id}
                    data-testid="calendar-day-sheet-event"
                    onClick={() => onEventClick(ev)}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      background: "var(--bg-primary)",
                      borderLeft: `3px solid ${ev.color || C.mosaic1}`,
                      border: `1px solid var(--border-subtle)`, borderLeftWidth: 3,
                      borderRadius: 6, padding: "10px 12px", marginBottom: 8, cursor: "pointer",
                    }}
                  >
                    <div style={{ color: "var(--text-primary)", fontSize: 13, fontWeight: 500 }}>{ev.title}</div>
                    <div style={{ color: "var(--color-accent-1)", fontSize: 11, marginTop: 2 }}>{hhmm(s)}{e ? `–${hhmm(e)}` : ""}</div>
                    {ev.location && <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>{ev.location}</div>}
                    {ev.event_type && ev.event_type !== "general" && (
                      <span style={{ display: "inline-block", marginTop: 6, padding: "2px 10px", borderRadius: 20, background: C.mosaic1, color: "var(--color-accent-1)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        {ev.event_type}
                      </span>
                    )}
                  </button>
                );
              })}
              {Object.entries(prayerTimes || {}).map(([name, t]) => (
                <div key={`p-${name}`} style={{ background: "var(--bg-primary)", borderLeft: "3px solid var(--color-accent-1)", border: "1px solid var(--border-subtle)", borderLeftWidth: 3, borderRadius: 6, padding: "8px 12px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{name}</span>
                  <span style={{ fontSize: 11, color: "var(--color-accent-1)", flex: 1 }}>{t}</span>
                  <span style={{ padding: "2px 8px", borderRadius: 20, background: C.mosaic1, color: "var(--color-accent-1)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em" }}>Prayer</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}

